import React, { useState } from 'react'
import _ from 'lodash-es'
import { useDrag } from '../../lib/hooks/useDrag'
import { mapValues, mapClampValues } from '../../lib/math-utils'
import { useParentSize } from '../../lib/hooks/useParentSize'
import { useDevice } from '../../lib/hooks/useDevice'

interface Props {
  className?: string
  min: number
  max: number
  step: number
  value: number
  maxAvailable?: number
  showTooltip?: boolean
  onChange: (value: number) => void
  diameter?: number
}

export const Slider: React.FC<Props> = ({
  className = '',
  min,
  max,
  step,
  value,
  maxAvailable,
  showTooltip = false,
  onChange,
  diameter = 24,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isMobile] = useDevice()

  const [ref, isDragging] = useDrag(({ deltaX }) => {
    const deltaValue = mapValues(deltaX, 0, width, 0, max - min)
    const deltaStep = Math.round(deltaValue / step) * step
    onChange(_.clamp(value + deltaStep, min, max))
  })

  const [{ width: widthOrNaN }] = useParentSize(ref)
  const width = Number.isNaN(widthOrNaN) ? 10 : widthOrNaN // Ugly fix, but working

  const x = mapClampValues(value, min, max, 0, 100)
  const scale = isDragging ? 1.2 : 1

  const dotted = maxAvailable !== undefined
  const actualMaxAvailable = maxAvailable ?? max

  return (
    <div className={`-slider relative w-100 ${className}`}>
      <div className="flex items-center overflow-visible" style={{ height: diameter }}>
        {dotted && (
          <div
            className={`bg-white br-pill absolute`}
            style={{
              width: `100%`,
              borderTop: '1px dotted var(--gray300)',
            }}
          />
        )}
        <div
          className={`${isDragging ? 'bg-blue500' : 'bg-gray500'} br-pill absolute`}
          style={{
            width: `${mapClampValues(actualMaxAvailable, min, max, 0, 100)}%`,
            height: 2,
            transition: 'transform 200ms',
          }}
        />
        <div
          ref={ref}
          className="absolute"
          style={{
            width: diameter,
            height: diameter,
            marginLeft: `${x}%`,
            transform: `translate(${-diameter / 2}px)`,
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {showTooltip && (
            <div className="black relative flex justify-center mt3 pt1">
              <div
                className={`absolute black fw5 transition user-select-none`}
                style={{ bottom: diameter + 4 }}
              >
                {value}
              </div>
            </div>
          )}
          <div
            className={'br-pill bg-white shadow-1 w-100 h-100 top-0 left-0 absolute'}
            style={{ transform: `scale(${scale})`, transition: 'transform 200ms' }}
          />
          {isMobile && (
            <div
              className={'br-pill w-100 h-100 top-0 left-0 absolute'}
              style={{
                // background: 'rgba(255,0,0,0.1)',
                transform: `scale(2.5)`,
                transformOrigin: `center center`,
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
